/**
 * AdminFirmDetail — owner view of a single firm.  Actions:
 *   * Mint a new seat batch (calls generate_firm_batch RPC via /admin/firms/{id}/batches)
 *   * Download batch codes CSV (auth'd stream)
 *   * Void a batch or a single code
 *   * Invite a firm user by email
 *
 * The list of batches is read via /firm/batches (RLS-scoped to caller's
 * firms — the owner will need to be in the firm's firm_users row to
 * see them here).  A future v2 pass can add /admin/firms/{id}/batches
 * (GET) for owners not in the firm; for now we mirror the firm view.
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiV2, downloadCsvV2 } from "@/lib/apiV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, Plus, Download, Prohibit, EnvelopeSimple,
} from "@phosphor-icons/react";

export default function AdminFirmDetail() {
  const { firmId } = useParams();
  const qc = useQueryClient();

  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [label, setLabel] = useState("");
  const [seatCount, setSeatCount] = useState(50);
  const [pricePerSeat, setPricePerSeat] = useState(39);
  const [expiresAt, setExpiresAt] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");

  // Reuse /firm/batches — RLS enforces firm membership; owner must be a
  // firm_user of this firm to see its batches.  For now this is fine
  // because owner creation typically follows a self-invite.
  const batches = useQuery({
    queryKey: ["firm-batches", firmId],
    queryFn: async () => {
      const { data } = await apiV2.get("/firm/batches");
      return (data.batches || []).filter((b) => b.firm_id === firmId);
    },
  });

  const mintBatch = useMutation({
    mutationFn: async () => {
      const body = {
        label: label.trim(),
        seat_count: Number(seatCount),
        price_per_seat_cents: Math.round(Number(pricePerSeat) * 100),
      };
      if (expiresAt) body.expires_at = expiresAt;
      const { data } = await apiV2.post(`/admin/firms/${firmId}/batches`, body);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Minted ${data.codes_generated} codes`);
      setShowBatchForm(false);
      setLabel("");
      qc.invalidateQueries({ queryKey: ["firm-batches", firmId] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Could not mint batch");
    },
  });

  const voidBatch = useMutation({
    mutationFn: async (batchId) => {
      const { data } = await apiV2.post(`/admin/batches/${batchId}/void`);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Voided ${data.voided} available codes`);
      qc.invalidateQueries({ queryKey: ["firm-batches", firmId] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Void failed");
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const { data } = await apiV2.post(`/admin/firms/${firmId}/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      return data;
    },
    onSuccess: () => {
      toast.success(`Invite sent to ${inviteEmail}`);
      setShowInviteForm(false);
      setInviteEmail("");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Invite failed");
    },
  });

  const onDownloadCsv = async (batchId) => {
    try {
      await downloadCsvV2(
        `/admin/batches/${batchId}/codes.csv`,
        `batch-${batchId.slice(0, 8)}-codes.csv`,
      );
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-firm-detail">
      <div>
        <Link to="/admin" className="text-sm text-[#2C4C3B] flex items-center gap-1 no-underline">
          <ArrowLeft size={14} /> All firms
        </Link>
        <h1 className="text-2xl font-semibold text-[#2C4C3B] mt-2">Firm detail</h1>
        <p className="text-sm text-slate-500 font-mono">{firmId}</p>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={() => setShowBatchForm((s) => !s)}
          className="bg-[#2C4C3B] hover:bg-[#22392C] text-white"
          data-testid="admin-mint-batch-btn"
        >
          <Plus size={16} className="mr-1" /> Mint batch
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowInviteForm((s) => !s)}
          data-testid="admin-invite-btn"
        >
          <EnvelopeSimple size={16} className="mr-1" /> Invite firm user
        </Button>
      </div>

      {showBatchForm && (
        <Card className="p-6" data-testid="admin-batch-form">
          <h2 className="font-medium mb-4">Mint seat batch</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Label</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="2027 tax season – 50 seats"
                data-testid="batch-label-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Seat count (1–1000)</Label>
              <Input
                type="number"
                min={1}
                max={1000}
                value={seatCount}
                onChange={(e) => setSeatCount(e.target.value)}
                data-testid="batch-seat-count-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Price per seat (CAD)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={pricePerSeat}
                onChange={(e) => setPricePerSeat(e.target.value)}
                data-testid="batch-price-input"
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Expires (optional — codes/paid_until clamp to this)</Label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="batch-expires-input"
                className="mt-1"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowBatchForm(false)}>Cancel</Button>
            <Button
              onClick={() => mintBatch.mutate()}
              disabled={mintBatch.isPending || !label.trim()}
              className="bg-[#2C4C3B] hover:bg-[#22392C] text-white"
              data-testid="batch-mint-submit"
            >
              {mintBatch.isPending ? "Minting…" : "Mint batch"}
            </Button>
          </div>
        </Card>
      )}

      {showInviteForm && (
        <Card className="p-6" data-testid="admin-invite-form">
          <h2 className="font-medium mb-4">Invite firm user</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="partner@firm.ca"
                data-testid="invite-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
                data-testid="invite-role-select"
              >
                <option value="staff">Staff</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowInviteForm(false)}>Cancel</Button>
            <Button
              onClick={() => invite.mutate()}
              disabled={invite.isPending || !inviteEmail.trim()}
              className="bg-[#2C4C3B] hover:bg-[#22392C] text-white"
              data-testid="invite-submit"
            >
              {invite.isPending ? "Sending…" : "Send magic-link invite"}
            </Button>
          </div>
        </Card>
      )}

      <Card className="divide-y divide-slate-100" data-testid="admin-batches-list">
        {batches.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Loading batches…</div>
        ) : (batches.data || []).length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No batches yet.</div>
        ) : (
          batches.data.map((b) => (
            <div key={b.id} className="p-4 flex items-center justify-between gap-3" data-testid={`batch-row-${b.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{b.label}</div>
                <div className="text-xs text-slate-500">
                  {b.redeemed_count} of {b.seat_count} used · {b.available_count} available
                  {b.void_count > 0 ? ` · ${b.void_count} void` : ""}
                  {b.expires_at ? ` · expires ${b.expires_at}` : " · no expiry"}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDownloadCsv(b.id)}
                  data-testid={`batch-csv-${b.id}`}
                >
                  <Download size={14} className="mr-1" /> CSV
                </Button>
                {b.available_count > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (window.confirm(`Void ${b.available_count} available codes? Redeemed subscriptions are safe.`)) {
                        voidBatch.mutate(b.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`batch-void-${b.id}`}
                  >
                    <Prohibit size={14} className="mr-1" /> Void batch
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
